import { useDubbingStore, useStore } from '@src/stores-v2/StoreContext';
import { useVideoCreatorStore } from '@src/stores-v2/VideoCreatorStoreContext';
import { extractErrorInfo } from '@src/utility/error';
import { LogManager } from '@src/utility/Logger';
import { useEffect } from 'react';

const logger = LogManager.getLogger('aiVoices:useEnsureDubbingFields');

// Ensures the workspace's AI-dubbing activation (DE3) plus its supported
// languages (DI19) are available on org-scoped pages that don't run the full
// `initializeData` story load — e.g. the Speaker Manager, which resolves its
// org via the lightweight `useResolveOrgFromUrlParams` (whose showcase lookups
// omit both). Without this, `organization.dubbingEnabled` stays `undefined`
// (so the "Manage AI Voices" entry points don't appear on first load) and
// `supportedLanguages` stays empty (so the Language Voice Defaults tab shows
// no languages) until Settings → AI Voices happens to hydrate them.
//
// Idempotent: the showcase fields are fetched only while unhydrated
// (`activatedAiVoices === undefined`), and the catalog hydrate() is a no-op once
// loaded for the showcase. Mirrors the fire-and-forget hydration in
// `VideoCreatorStore.initializeWithStory`.
export function useEnsureDubbingFields(): void {
  const videoCreator = useVideoCreatorStore();
  const { datoClientStore } = useStore();
  const dubbingStore = useDubbingStore();

  const orgId = videoCreator.organization?.id;
  // Dubbing fields land as a set; `activatedAiVoices` is undefined until then.
  const needsFields =
    videoCreator.organization?.activatedAiVoices === undefined;

  useEffect(() => {
    if (!orgId) return undefined;

    // Catalog (the pickable pool) — idempotent per showcase.
    void dubbingStore.hydrate(orgId);

    if (!needsFields) return undefined;
    const albumRepository = datoClientStore.albumRepository;
    if (!albumRepository) return undefined;
    let cancelled = false;
    // Fetch the dubbing activation (DE3) and the supported languages (DI19,
    // carried by the translation-fields query) together — the Language Voice
    // Defaults tab iterates the workspace's languages.
    void Promise.all([
      albumRepository.fetchDubbingFieldsById(orgId),
      albumRepository.fetchTranslationFieldsById(orgId),
    ])
      .then(([dubbingFields, translationFields]) => {
        if (cancelled) return;
        // Apply through a store action (it guards against a workspace switch
        // mid-flight) rather than assigning organization from the hook.
        videoCreator.applyDubbingFields(orgId, {
          ...dubbingFields,
          supportedLanguages: translationFields.supportedLanguages,
        });
      })
      .catch(err => {
        logger.warn(
          'Failed to hydrate showcase dubbing/language fields',
          extractErrorInfo(err),
        );
      });
    return () => {
      cancelled = true;
    };
  }, [orgId, needsFields, dubbingStore, datoClientStore, videoCreator]);
}
